import { useState, useEffect } from "react";
import { useIntercom } from "react-use-intercom";
import { useLocation } from "react-router-dom";

import { getAccountRegistrationState, getAuthUser } from "../api/auth";
import { useHistoryWithPathBasedReload } from "../components/App/LinkWithPathBasedReload";
import { PATHNAMES } from "../constants/pathnames";

const AuthContext = React.createContext(undefined);

const AuthProvider = ({ children, mockUser }) => {
  const [user, setUser] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const { update } = useIntercom();

  useEffect(() => {
    if (mockUser !== undefined) {
      setUser(mockUser);
    } else {
      fetchUser();
    }
  }, []);

  useEffect(() => {
    if (Object.values(user).length > 0) {
      const intercomProps = {
        email: user.email,
        userHash: user.intercom_hmac,
      };
      update(intercomProps);
    }
  }, [user]);

  const fetchUser = async ({ ignoreLoading } = {}) => {
    try {
      if (!ignoreLoading) {
        setLoading(true);
      }

      const { data } = await getAuthUser();

      if (data?.user) {
        try {
          const { data: accountRegistrationState } = await getAccountRegistrationState();
          data.user.accountRegistrationState = accountRegistrationState;
        } catch (e) {
          // NOTE: fall back to minimum_profile_completed, see https://github.com/oula-health/oula-ui/blob/c695ddfa7e4b06e2c8a725bf9adec74fee8fe44c/src/components/Navigator/Navigator.jsx#L39
        }
      }

      setIsAuthenticated(data?.user?.id ? true : false);
      setUser(data?.user ?? {});
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return React.useContext(AuthContext);
};

/**
 * redirect authenticated users to original route from pre login
 * Reads redirect_auth query param and navigates to that path after login.
 * Mirrors Portal's useRedirectIfLoggedIn implementation.
 */
export const useRedirectIfLoggedIn = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const history = useHistoryWithPathBasedReload();

  useEffect(() => {
    if (isAuthenticated) {
      // Parse the redirect_auth parameter
      const searchParams = new URLSearchParams(location.search);
      const redirectAuthParam = searchParams.get('redirect_auth');
      const redirectPath = redirectAuthParam ? decodeURIComponent(redirectAuthParam) : null;

      const isValidRedirect = redirectPath &&
        redirectPath.startsWith('/') &&
        !redirectPath.startsWith('//') &&
        redirectPath !== PATHNAMES.LOGIN;

      const destination = isValidRedirect ? redirectPath : PATHNAMES.DASHBOARD;
      history.push(destination);
    }
  }, [isAuthenticated, location.search, history]);
};

export default AuthProvider;
